import { z } from 'zod';

/**
 * These mirror the PostgreSQL enum types one-for-one (see
 * docs/02-DATABASE-SCHEMA.md §3). `packages/db` declares the same values via
 * Drizzle's `pgEnum`; a drift test in the db package asserts the two lists
 * match, so a value added in SQL but not here fails CI.
 */

export const CompanyPlan = z.enum(['trial', 'starter', 'business', 'enterprise']);
export type CompanyPlan = z.infer<typeof CompanyPlan>;

export const CompanyStatus = z.enum(['active', 'suspended', 'cancelled']);
export type CompanyStatus = z.infer<typeof CompanyStatus>;

export const ActiveStatus = z.enum(['active', 'inactive']);
export type ActiveStatus = z.infer<typeof ActiveStatus>;

export const UserStatus = z.enum(['active', 'inactive', 'suspended', 'pending']);
export type UserStatus = z.infer<typeof UserStatus>;

export const RoleSlug = z.enum([
  'super_admin',
  'company_admin',
  'hr',
  'manager',
  'supervisor',
  'employee',
]);
export type RoleSlug = z.infer<typeof RoleSlug>;

export const SettingType = z.enum(['string', 'int', 'float', 'bool', 'json', 'array', 'time']);
export type SettingType = z.infer<typeof SettingType>;

export const AuditEvent = z.enum([
  'created',
  'updated',
  'deleted',
  'restored',
  'login',
  'logout',
  'failed_login',
  'permission_changed',
  'token_revoked',
  'export',
  'impersonate',
  'settings_changed',
  'partition_rotated',
]);
export type AuditEvent = z.infer<typeof AuditEvent>;

// --- Tracking (schemas land in M5; the vocabulary is fixed now so the
//     mobile app and panel can be written against it) ---

export const MotionType = z.enum([
  'still',
  'walking',
  'running',
  'on_bicycle',
  'in_vehicle',
  'on_foot',
  'unknown',
]);
export type MotionType = z.infer<typeof MotionType>;

export const PointStatus = z.enum(['accepted', 'rejected', 'flagged']);
export type PointStatus = z.infer<typeof PointStatus>;

export const RejectReason = z.enum([
  'low_accuracy',
  'mock_location',
  'teleport',
  'clock_skew',
  'no_open_session',
  'duplicate',
  'stale',
  'out_of_shift',
  'invalid_coordinates',
  'zero_island',
]);
export type RejectReason = z.infer<typeof RejectReason>;

export const NetworkType = z.enum(['wifi', 'cellular', 'ethernet', 'vpn', 'none', 'unknown']);
export type NetworkType = z.infer<typeof NetworkType>;

export const LocationProvider = z.enum(['gps', 'network', 'fused', 'passive', 'unknown']);
export type LocationProvider = z.infer<typeof LocationProvider>;

export const SessionStatus = z.enum(['active', 'ended', 'stale', 'force_closed']);
export type SessionStatus = z.infer<typeof SessionStatus>;

export const AttendanceStatus = z.enum([
  'present',
  'absent',
  'late',
  'half_day',
  'on_leave',
  'holiday',
  'weekly_off',
  'missed_shift',
]);
export type AttendanceStatus = z.infer<typeof AttendanceStatus>;

export const GeofenceShape = z.enum(['circle', 'polygon']);
export type GeofenceShape = z.infer<typeof GeofenceShape>;

export const GeofenceType = z.enum([
  'office',
  'warehouse',
  'customer_site',
  'project_site',
  'factory',
  'restricted',
  'other',
]);
export type GeofenceType = z.infer<typeof GeofenceType>;

export const AlertSeverity = z.enum(['info', 'warning', 'critical']);
export type AlertSeverity = z.infer<typeof AlertSeverity>;

export const AlertStatus = z.enum([
  'open',
  'acknowledged',
  'resolved',
  'auto_resolved',
  'suppressed',
]);
export type AlertStatus = z.infer<typeof AlertStatus>;
